/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import { getTextStyle } from '@airasia-phoenix/react-native/utils/utils';
import { pd } from '@airasia-phoenix/react-native/tokens';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import DividerView from '@airasia-phoenix/react-native/components/DividerView/index';
import {
  DividerDirection,
  DividerType,
} from '@airasia-phoenix/react-native/components/DividerView/types';
import { styles } from './styles';
import {
  shouldShowDevAuthToggle,
  toggleV6Auth,
} from '../../utils/authVersionUtils';
import { getSafeLocalStorageItem } from '../../utils/safeLocalStorage';
import { useAppConfig } from '@airasia-rn/ui';

interface AuthWidgetVersionToggleProps {
  isMobile?: boolean;
  darkModeToggleEnabled?: boolean;
}

const AuthWidgetVersionToggleComponent: React.FC<
  AuthWidgetVersionToggleProps
> = ({ isMobile = false, darkModeToggleEnabled = false }) => {
  const { theme } = useUnistyles();
  const { config } = useAppConfig();
  // Starts on the server's value: reading localStorage during render fails
  // hydration of the whole page when the flag is set.
  const [versionText, setVersionText] = useState('v5');

  useEffect(() => {
    if (getSafeLocalStorageItem('showV6Auth')) setVersionText('v6');
  }, []);

  const textStyle = useMemo(
    () =>
      getTextStyle(
        pd.sys.typescale.sub_body_text,
        pd.sys.colour.content_primary,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `theme` re-triggers pd.sys.* colour resolution, which eslint can't see
    [theme],
  );

  const pressableStyle = useMemo(
    () =>
      isMobile
        ? [styles.mobileDarkMode, { right: darkModeToggleEnabled ? 56 : 16 }]
        : styles.account,
    [isMobile, darkModeToggleEnabled],
  );

  const textViewStyle = useMemo(
    () => (isMobile ? textStyle : [textStyle, styles.accountLabel]),
    [isMobile, textStyle],
  );

  const showDevAuthToggle = shouldShowDevAuthToggle(config.env);

  if (!showDevAuthToggle) return null;

  // hiding this temp flag in mobile due to space crunch
  if (isMobile) return null;

  return (
    <>
      {!isMobile && (
        <View style={styles.dividerWrapper}>
          <DividerView
            type={DividerType.Solid}
            direction={DividerDirection.Vertical}
            thickness={1}
            height={32}
          />
        </View>
      )}
      <Pressable onPress={toggleV6Auth} style={pressableStyle}>
        <TextView text={versionText} style={textViewStyle} />
      </Pressable>
    </>
  );
};

export const AuthWidgetVersionToggle = React.memo(
  AuthWidgetVersionToggleComponent,
);

AuthWidgetVersionToggle.displayName = 'AuthWidgetVersionToggle';
